/**
 * VendedorProfile — perfil do vendedor: foto, nome, email (read-only),
 * WhatsApp, telefone, endereço + troca de senha.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Mail, Phone, MapPin, Camera, Save,
  MessageCircle, Lock, Eye, EyeOff, KeyRound
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ThemeColorsCard from '../components/ThemeColorsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VendedorProfile() {
  const navigate = useNavigate();
  const { user, updateUser, loading: authLoading } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: '', phone: '', whatsapp: '', endereco: '', profile_image: '', theme_colors: {}
  });
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ c: false, n: false, cf: false });
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'vendedor' && user.role !== 'admin') { navigate('/login'); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/vendedor/me/profile`);
        setForm({
          name: r.data.name || '',
          phone: r.data.phone || '',
          whatsapp: r.data.whatsapp || '',
          endereco: r.data.endereco || '',
          profile_image: r.data.profile_image || '',
          theme_colors: r.data.theme_colors || {},
        });
        setSeller(r.data.seller);
      } catch {
        toast.error('Erro ao carregar perfil');
      }
      setLoading(false);
    })();
  }, [authLoading, user, navigate]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Imagem muito grande (máx 2MB)'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setForm(prev => ({ ...prev, profile_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vendedor/me/profile`, form);
      if (updateUser) updateUser({ name: form.name, phone: form.phone, profileImage: form.profile_image });
      toast.success('Perfil atualizado!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!pw.current || !pw.next) { toast.error('Preencha as senhas'); return; }
    if (pw.next !== pw.confirm) { toast.error('Nova senha e confirmação não coincidem'); return; }
    if (pw.next.length < 6) { toast.error('Senha nova precisa ter no mínimo 6 caracteres'); return; }
    setChangingPw(true);
    try {
      await axios.post(`${API}/vendedor/me/change-password`, {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success('Senha atualizada!');
      setPw({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao trocar senha');
    }
    setChangingPw(false);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/vendedor/dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-testid="back-to-vendedor-dashboard">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="flex-1 font-bold text-gray-900 dark:text-white">Meu Perfil</h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
            data-testid="save-vendedor-profile-btn"
          >
            {saving ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Avatar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center gap-3">
          <div className="relative">
            {form.profile_image ? (
              <img src={form.profile_image} alt="" className="w-24 h-24 rounded-full object-cover border-4 border-blue-500" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center border-4 border-blue-500">
                <User className="w-10 h-10 text-blue-600" />
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-full shadow-lg"
              data-testid="vendedor-change-avatar-btn"
            >
              <Camera className="w-3.5 h-3.5" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
          </div>
          <p className="text-xs text-gray-500">Clique no ícone para mudar a foto</p>
        </div>

        {/* Dados pessoais */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-3">
          <h2 className="font-bold text-sm text-gray-900 dark:text-white">Dados Pessoais</h2>
          <Field icon={User} label="Nome">
            <input type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-vendedor" data-testid="vendedor-profile-name" />
          </Field>
          <Field icon={Mail} label="E-mail (não editável)">
            <input type="email" value={user?.email || ''} disabled
              className="input-vendedor bg-gray-100 dark:bg-gray-900/40 text-gray-500" />
          </Field>
          <Field icon={MessageCircle} label="WhatsApp">
            <input type="tel" value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              placeholder="(11) 99999-9999" className="input-vendedor" data-testid="vendedor-profile-whatsapp" />
          </Field>
          <Field icon={Phone} label="Telefone (opcional)">
            <input type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(11) 3333-3333" className="input-vendedor" data-testid="vendedor-profile-phone" />
          </Field>
          <Field icon={MapPin} label="Endereço">
            <textarea rows={2} value={form.endereco}
              onChange={(e) => setForm({ ...form, endereco: e.target.value })}
              placeholder="Rua, número, bairro, cidade..."
              className="input-vendedor" data-testid="vendedor-profile-endereco" />
          </Field>
        </div>

        {/* Dados da empresa (só leitura) */}
        {seller && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-2">
            <h2 className="font-bold text-sm text-gray-900 dark:text-white">Cadastro de Vendedor</h2>
            <ReadOnly label="Nome no sistema" value={seller.nome} />
            <ReadOnly label="Comissão padrão" value={`${seller.percentual_comissao || 0}%`} />
            <p className="text-[10px] text-gray-500 mt-2">
              Para alterar dados de cadastro (nome no sistema, comissão), fale com o admin.
            </p>
          </div>
        )}

        {/* Troca de senha */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-3" data-testid="vendedor-change-password-card">
          <h2 className="font-bold text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <KeyRound className="w-4 h-4 text-amber-500" />
            Trocar Senha
          </h2>
          <PwField label="Senha atual" value={pw.current} show={showPw.c}
            onToggle={() => setShowPw(p => ({ ...p, c: !p.c }))}
            onChange={(v) => setPw({ ...pw, current: v })}
            testId="pw-current" />
          <PwField label="Nova senha (mín. 6 caracteres)" value={pw.next} show={showPw.n}
            onToggle={() => setShowPw(p => ({ ...p, n: !p.n }))}
            onChange={(v) => setPw({ ...pw, next: v })}
            testId="pw-new" />
          <PwField label="Confirmar nova senha" value={pw.confirm} show={showPw.cf}
            onToggle={() => setShowPw(p => ({ ...p, cf: !p.cf }))}
            onChange={(v) => setPw({ ...pw, confirm: v })}
            testId="pw-confirm" />
          <button
            onClick={handleChangePassword}
            disabled={changingPw}
            className="w-full sm:w-auto px-4 py-2 text-sm font-semibold bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white rounded-lg flex items-center justify-center gap-2"
            data-testid="submit-change-password"
          >
            {changingPw ? 'Alterando...' : (<><Lock className="w-4 h-4" /> Trocar Senha</>)}
          </button>
        </div>

        {/* Personalização de cores */}
        <ThemeColorsCard
          endpoint="/vendedor/me/theme-colors"
          initialColors={form.theme_colors}
          onSaved={(c) => setForm(f => ({ ...f, theme_colors: c }))}
          testidPrefix="vendedor-theme"
        />
      </div>

      <style>{`.input-vendedor{width:100%;padding:0.5rem 0.75rem;font-size:0.875rem;border:1px solid rgb(209 213 219);border-radius:0.5rem;background:white;outline:none}
      .dark .input-vendedor{background:rgb(17 24 39);border-color:rgb(55 65 81);color:white}
      .input-vendedor:focus{box-shadow:0 0 0 2px rgba(37,99,235,.4)}`}</style>
    </div>
  );
}

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </label>
    {children}
  </div>
);

const ReadOnly = ({ label, value }) => (
  <div className="flex justify-between text-sm gap-3">
    <span className="text-gray-600 dark:text-gray-300">{label}:</span>
    <span className="text-gray-900 dark:text-white font-medium truncate">{value || '-'}</span>
  </div>
);

const PwField = ({ label, value, show, onToggle, onChange, testId }) => (
  <div>
    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">{label}</label>
    <div className="relative">
      <input type={show ? 'text' : 'password'} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-vendedor pr-9"
        data-testid={testId} />
      <button type="button" onClick={onToggle}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);
